import React, { useState, useEffect, useRef, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import { Dices, Sparkles, TrendingUp } from "lucide-react";
import { Dashboard } from "./Dashboard";
import { Dice } from "./Dice";
import { ScenarioCard } from "./ScenarioCard";
import { GrowthMascot } from "./GrowthMascot";
import { Button } from "../ui/button";
import { api, fmt, fmtDelta, CATEGORIES } from "../../lib/game";
import { celebrate } from "../../lib/engage";

const ROLL_MS = 1650;

export const GameBoard = ({ session, totalRounds, onUpdate, onComplete }) => {
  const [phase, setPhase] = useState("ready"); // ready | rolling | card | flipped
  const [face, setFace] = useState(1);
  const [spins, setSpins] = useState(3);
  const [card, setCard] = useState(null);
  const [dividend, setDividend] = useState(null); // {round, growth}
  const [rollAgain, setRollAgain] = useState(false);
  const [flashKeys, setFlashKeys] = useState([]);
  const [floatDelta, setFloatDelta] = useState(null);
  const [deciding, setDeciding] = useState(false);
  const [shake, setShake] = useState(false);

  const processedRound = useRef(0);
  const mounted = useRef(true);
  useEffect(() => {
    mounted.current = true;
    return () => { mounted.current = false; };
  }, []);

  const startRound = useCallback(async (sess) => {
    if (processedRound.current === sess.current_round) return;
    processedRound.current = sess.current_round;
    setPhase("ready");
    setCard(null);
    setRollAgain(false);
    if (sess.current_round >= 2) {
      try {
        const { data } = await api.post(`/sessions/${sess.id}/dividend`);
        if (data.growth > 0) {
          setDividend({ round: sess.current_round, growth: data.growth });
          onUpdate(data.session);
          setTimeout(() => mounted.current && setDividend(null), 4200);
        }
      } catch (e) {
        // dividend is non-blocking
      }
    }
  }, [onUpdate]);

  useEffect(() => {
    if (session.status === "in_progress") startRound(session);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [session.current_round, session.status]);

  const handleRoll = async () => {
    if (phase === "rolling") return;
    setPhase("rolling");
    setRollAgain(false);
    setSpins((s) => s + 3);
    try {
      const { data } = await api.post(`/sessions/${session.id}/roll`);
      setFace(data.face);
      setTimeout(() => {
        if (!mounted.current) return;
        if (data.roll_again) {
          setRollAgain(true);
          setPhase("ready");
        } else {
          setCard(data.card);
          setPhase("card");
        }
      }, ROLL_MS);
    } catch (e) {
      toast.error("Couldn't roll the die. Please try again.");
      setPhase("ready");
    }
  };

  const handleChoose = async (choiceIndex) => {
    if (deciding || !card) return;
    setDeciding(true);
    const prevNW = session.net_worth;
    try {
      const { data } = await api.post(`/sessions/${session.id}/decision`, {
        round_number: session.current_round,
        category: card.category,
        card_id: card.id,
        choice_index: choiceIndex,
      });
      const d = data.delta;
      const keys = [];
      if (d.cash) keys.push("cash");
      if (d.investments) keys.push("investments");
      if (d.debts) keys.push("debts");
      keys.push("net_worth");
      setFlashKeys(keys);
      setTimeout(() => mounted.current && setFlashKeys([]), 1000);

      const nwDelta = data.session.net_worth - prevNW;
      setFloatDelta(nwDelta);
      setTimeout(() => mounted.current && setFloatDelta(null), 1800);

      // Playful micro-interactions: celebrate good moves, shake/pulse on risky ones
      if (nwDelta > 0 || d.debts < 0) {
        celebrate();
      } else if (nwDelta < 0 || d.debts > 0) {
        setShake(true);
        setTimeout(() => mounted.current && setShake(false), 600);
      }

      const bits = [];
      if (d.cash) bits.push(`${fmtDelta(d.cash)} cash`);
      if (d.investments) bits.push(`${fmtDelta(d.investments)} invest`);
      if (d.debts) bits.push(`${fmtDelta(d.debts)} debt`);
      toast.success("Decision locked in", { description: bits.join("  ·  ") || "No change" });

      setPhase("ready");
      setCard(null);
      setDeciding(false);

      if (data.session.status === "complete") {
        onComplete(data.session);
      } else {
        onUpdate(data.session);
      }
    } catch (e) {
      toast.error("Couldn't apply your decision. Please try again.");
      setDeciding(false);
    }
  };

  const meta = card ? CATEGORIES[card.category] : null;

  return (
    <div className="bg-grid min-h-[calc(100vh-61px)] px-4 py-6 sm:py-8">
      <div className="max-w-3xl mx-auto">
        <div className="relative">
          <Dashboard session={session} flashKeys={flashKeys} />
          <AnimatePresence>
            {floatDelta !== null && (
              <motion.div
                key="floatdelta"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="pointer-events-none absolute -top-2 right-2 z-20"
              >
                <span
                  className={`delta-float inline-block font-mono font-bold text-lg px-3 py-1 rounded-lg ${
                    floatDelta >= 0
                      ? "bg-emerald-500/20 text-emerald-300 border border-emerald-500/40"
                      : "bg-red-500/20 text-red-300 border border-red-500/40"
                  }`}
                >
                  {fmtDelta(floatDelta)} net
                </span>
              </motion.div>
            )}
          </AnimatePresence>
        </div>

        {/* Growth mascot (the category-coverage tracker is facilitator/admin-only) */}
        <div className="mt-4">
          <GrowthMascot session={session} />
        </div>

        {/* Dividend banner */}
        <AnimatePresence>
          {dividend && (
            <motion.div
              initial={{ opacity: 0, y: -16, scale: 0.96 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: -10 }}
              transition={{ type: "spring", stiffness: 300, damping: 22 }}
              data-testid="dividend-banner"
              className="mt-4 flex items-center gap-3 rounded-2xl border border-purple-500/40 bg-purple-500/10 px-4 py-3"
            >
              <div className="w-9 h-9 rounded-lg bg-purple-500/20 flex items-center justify-center shrink-0">
                <TrendingUp className="w-4 h-4 text-purple-300" />
              </div>
              <p className="text-sm text-purple-100">
                <span className="font-semibold">Round {dividend.round} dividend:</span> your investments
                grew by <span className="font-mono font-bold text-purple-300">{fmt(dividend.growth)}</span> in
                compound returns.
              </p>
              <Sparkles className="w-4 h-4 text-purple-300 ml-auto shrink-0" />
            </motion.div>
          )}
        </AnimatePresence>

        {/* Center stage */}
        <div className={`mt-6 sm:mt-8 ${shake ? "shake" : ""}`}>
          {phase === "ready" || phase === "rolling" ? (
            <div className="rounded-3xl border border-slate-800 bg-slate-900/60 backdrop-blur p-8 text-center shadow-2xl">
              <p className="text-xs font-semibold uppercase tracking-widest text-slate-500 mb-1">
                Round {session.current_round} of {totalRounds}
              </p>
              <h2 className="font-heading text-2xl font-bold text-slate-100 mb-6">
                {phase === "rolling" ? "Rolling…" : rollAgain ? "Roll Again!" : "Roll the die"}
              </h2>

              <div className="flex justify-center mb-6">
                <div className={spins ? "dice-rolling-wrap" : ""}>
                  <Dice face={face} spins={spins} />
                </div>
              </div>

              {rollAgain && phase === "ready" && (
                <p className="text-sm text-yellow-400 mb-4 font-medium">
                  You landed on ROLL AGAIN — take another roll, free of charge!
                </p>
              )}

              <Button
                data-testid="roll-dice-btn"
                onClick={handleRoll}
                disabled={phase === "rolling"}
                className="h-12 px-8 bg-gradient-to-r from-emerald-500 to-teal-600 hover:from-emerald-400 hover:to-teal-500 text-slate-950 font-bold text-base rounded-xl shadow-lg shadow-emerald-500/25 disabled:opacity-60"
              >
                <Dices className="w-5 h-5 mr-1" />
                {phase === "rolling" ? "Rolling…" : "Roll the Die"}
              </Button>

              <div className="mt-6 flex flex-wrap justify-center gap-1.5">
                {["SAVE", "EARN", "INVEST", "SPEND", "LIFE_EVENT", "DEBT"].map((k) => {
                  const m = CATEGORIES[k];
                  return (
                    <span key={k} className={`text-[10px] font-semibold uppercase tracking-wider px-2 py-0.5 rounded-full border ${m.badge}`}>
                      {m.face} · {m.label}
                    </span>
                  );
                })}
              </div>
            </div>
          ) : (
            card && (
              <div>
                <p className="text-center text-xs font-semibold uppercase tracking-widest text-slate-500 mb-4">
                  Round {session.current_round} of {totalRounds} · {meta.label} card drawn
                </p>
                <ScenarioCard
                  card={card}
                  flipped={phase === "flipped"}
                  onFlip={() => setPhase("flipped")}
                  onChoose={handleChoose}
                  disabled={deciding}
                />
                {phase === "card" && (
                  <p className="text-center text-sm text-slate-500 mt-4">Tap the card to see your options</p>
                )}
              </div>
            )
          )}
        </div>
      </div>
    </div>
  );
};
